import type { Gateway, InstallationTask, Sensor } from '@si/types';
import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  installationId: string;
  editedSensors?: Sensor[];
  editedGateways?: Gateway[];
  editedInstallationTasks: InstallationTask[];
  newInstallationTasks: InstallationTask[];
  removedInstallationTaskIds: number[];
}

export function useUpdateInstallationAddAndMove() {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: Params) =>
      post({
        url: `/projects/${params.projectId}/installations/${params.installationId}/add-and-move`,
        body: {
          ...params,
          editedSensors: params.editedSensors ?? [],
          editedGateways: params.editedGateways ?? [],
          editedInstallationTasks: params.editedInstallationTasks,
          newInstallationTasks: params.newInstallationTasks.map((task) => ({
            ...task,
            installationTaskId: -1,
          })),
        },
      }),
    onSuccess: (_, { projectId, installationId }) => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'sensors') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === projectId;
        },
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getGateways(projectId, installationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getGateways(projectId, 'installationId'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallationTasks(projectId, installationId),
      });
    },
  });

  const updateInstallationAddAndMove = useCallback((params: Params) => mutate(params), [mutate]);
  const updateInstallationAddAndMoveAsync = useCallback(
    (params: Params) => mutateAsync(params),
    [mutateAsync]
  );

  return {
    updateInstallationAddAndMove,
    updateInstallationAddAndMoveAsync,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
